import { getStatesByCountryCode } from '@common/services';
import { postalAddressesCountryDefinitions } from '@entrata/utils';

export interface AddressStateOption {
  value: string;
  label: string;
}

export const formatAddressFieldLabel = (value: string | undefined, fallback: string) =>
  value?.replace(/\b\w/g, (letter) => letter.toUpperCase()) ?? fallback;

export function normalizeCountryCode(country: string | undefined) {
  const normalized = country?.trim().toUpperCase();
  if (!normalized) return 'US';
  if (postalAddressesCountryDefinitions[normalized]) return normalized;

  const match = Object.entries(postalAddressesCountryDefinitions).find(
    ([, definition]) => definition.name.trim().toUpperCase() === normalized,
  );
  return match?.[0] ?? normalized;
}

export function getPostalCodePattern(pattern: string | undefined) {
  if (!pattern) return undefined;

  try {
    return new RegExp(`^(?:${pattern})$`, 'i');
  } catch {
    return undefined;
  }
}

export function shouldUseUsPostalCodeBehavior(countryCode?: string) {
  return normalizeCountryCode(countryCode) === 'US';
}

export function hasCountrySpecificStateOptions(countryCode?: string) {
  return getStatesByCountryCode(normalizeCountryCode(countryCode)).length > 0;
}

export function getAddressStateOptions(
  stateOptions: AddressStateOption[] | undefined,
  countryCode?: string,
  {
    isLegacyUsAddress = false,
    subdivisionDepth,
  }: { isLegacyUsAddress?: boolean; subdivisionDepth?: number } = {},
): AddressStateOption[] {
  if (stateOptions?.length) return stateOptions;
  if (stateOptions && (subdivisionDepth ?? 0) <= 0) return stateOptions;

  // Applications metadata may omit `state_options` while still declaring a
  // subdivision (for example, local Core returns subdivision_depth=1 for IN
  // without embedding the options). Use the shared country catalog in that
  // case. An explicit [] is retained when Core confirms there is no
  // subdivision; an empty list with subdivision metadata is incomplete.
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  const options = getStatesByCountryCode(normalizedCountryCode);
  if (options.length) return options;

  // Retain the legacy US fallback for configurations that have no country
  // field and for US addresses. Non-US API failures must not receive US
  // states as a fallback.
  if (isLegacyUsAddress || shouldUseUsPostalCodeBehavior(countryCode)) {
    return getStatesByCountryCode('US');
  }

  return [];
}

export function shouldShowAddressStateField({
  subdivisionDepth,
  hasStateOptions,
  isConfigured,
  isLoading,
  isError,
}: {
  subdivisionDepth: number | undefined;
  hasStateOptions: boolean;
  isConfigured: boolean;
  isLoading: boolean;
  isError: boolean;
}) {
  const hasSubdivision = (subdivisionDepth ?? 0) > 0;
  const preserveConfiguredField = isConfigured && (isLoading || isError);

  return (hasSubdivision || preserveConfiguredField) && (hasStateOptions || isLoading || isError);
}

export function shouldShowAddressStateError({
  isError,
  countryCode,
  isLegacyUsAddress,
  hasCountrySpecificOptions,
}: {
  isError: boolean;
  countryCode?: string;
  isLegacyUsAddress: boolean;
  hasCountrySpecificOptions: boolean;
}) {
  return (
    isError &&
    !isLegacyUsAddress &&
    !shouldUseUsPostalCodeBehavior(countryCode) &&
    !hasCountrySpecificOptions
  );
}
